import { useState, useEffect } from "react";
import { 
  X, RefreshCw, TrendingUp, Users, Target, BarChart2, 
  MessageSquare, Share2, Globe, Mail,
  ArrowUp, ArrowDown, Activity, Megaphone,
  Facebook, Linkedin, Instagram, Youtube, Sparkles, Settings, MapPin, Zap
} from "lucide-react";
import SanIAChat from "./components/SanIAChat";
import GoogleAdsSection from "./components/GoogleAdsSection";
import GoogleAdsROI from "./components/GoogleAdsROI";
import GoogleAdsSettings from "./components/GoogleAdsSettings";
import GeoLeaksPanel from "./components/GeoLeaksPanel";
import BidOptimizerPanel from "./components/BidOptimizerPanel";
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const safeJson = async (res) => {
  try { return await res.json(); } catch (e) {
    if (e.message?.includes("body stream already read") || e.message?.includes("locked")) {
      try { return await res.clone().json(); } catch { return {}; }
    }
    return {};
  }
};


// Helper functions to avoid Babel .map() issue
function renderKPICards(stats) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <KPICard icon={Users} label="Leads ce mois" value={stats.leads_this_month} change={stats.leads_growth} color="pink" />
      <KPICard icon={TrendingUp} label="Taux conversion" value={stats.conversion_rate + "%"} change={2.3} color="green" />
      <KPICard icon={Target} label="Coût/Lead moyen" value={stats.avg_cost_per_lead + "€"} change={-5.2} color="blue" invertChange />
      <KPICard icon={Globe} label="Visites site" value={stats.website_visits.toLocaleString()} change={8.7} color="purple" />
    </div>
  );
}

function renderBarChart() {
  const months = ['Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
  const values = [85, 92, 108, 98, 115, 124];
  const bars = [];
  for (let i = 0; i < values.length; i++) {
    bars.push(
      <div key={i} className="flex-1 flex flex-col items-center gap-2">
        <div 
          className="w-full bg-gradient-to-t from-pink-500/50 to-pink-500/20 rounded-t-lg transition-all hover:from-pink-500/70 hover:to-pink-500/40"
          style={{ height: (values[i] / 130) * 100 + '%' }}
        />
        <span className="text-xs text-slate-400">{months[i]}</span>
        <span className="text-xs font-medium text-white">{values[i]}</span>
      </div>
    );
  }
  return bars;
}

function renderSourceBars(sourceStats) {
  const items = [];
  const colors = ['pink', 'blue', 'green', 'purple', 'orange', 'cyan', 'yellow', 'red', 'indigo', 'emerald'];
  let maxCount = 50;
  for (let i = 0; i < sourceStats.length; i++) {
    if (sourceStats[i].count > maxCount) maxCount = sourceStats[i].count;
  }
  
  for (let i = 0; i < Math.min(sourceStats.length, 10); i++) {
    const source = sourceStats[i];
    const percentage = Math.round((source.count / maxCount) * 100);
    items.push(
      <div key={source.name || i} className="flex items-center gap-4">
        <div className="w-32 text-sm text-slate-400 truncate">{source.name || "(Non défini)"}</div>
        <div className="flex-1 h-6 bg-slate-700/50 rounded-lg overflow-hidden">
          <div className="h-full bg-pink-500/50 rounded-lg" style={{ width: percentage + '%' }} />
        </div>
        <div className="w-16 text-right">
          <span className="text-sm font-medium">{source.count}</span>
          <span className="text-xs text-slate-500 ml-1">leads</span>
        </div>
      </div>
    );
  }
  return items;
}

function renderCampaigns(campaigns) {
  const items = [];
  for (let i = 0; i < campaigns.length; i++) {
    const campaign = campaigns[i];
    items.push(
      <div key={campaign.id} className="px-6 py-4 hover:bg-slate-700/20 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={campaign.status === 'active' ? "w-3 h-3 rounded-full bg-green-500" : "w-3 h-3 rounded-full bg-amber-500"} />
            <div>
              <h4 className="font-medium">{campaign.name}</h4>
              <p className="text-sm text-slate-400">{campaign.leads} leads générés</p>
            </div>
          </div>
          <div className="flex items-center gap-8 text-sm">
            <div className="text-center">
              <p className="text-slate-400">Budget</p>
              <p className="font-medium">{campaign.budget > 0 ? campaign.budget + '€' : 'Gratuit'}</p>
            </div>
            <div className="text-center">
              <p className="text-slate-400">Dépensé</p>
              <p className="font-medium">{campaign.spent > 0 ? campaign.spent + '€' : '-'}</p>
            </div>
            <div className="text-center">
              <p className="text-slate-400">ROI</p>
              <p className={campaign.roi > 100 ? "font-medium text-green-400" : "font-medium text-amber-400"}>
                {campaign.roi > 0 ? campaign.roi + '%' : '-'}
              </p>
            </div>
            <span className={campaign.status === 'active' ? "px-2 py-1 rounded text-xs bg-green-500/20 text-green-400" : "px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-400"}>
              {campaign.status === 'active' ? 'Actif' : 'En pause'}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return items;
}

function renderContentIdeas() {
  const ideas = [
    "📹 Témoignage client: M. Dupont et sa salle de bain adaptée",
    "📸 Avant/Après: Transformation d'une cuisine pour PMR",
    "📝 Article: Les aides financières MaPrimeAdapt' en 2025",
    "🎥 Tutoriel: Comment monter un dossier ANAH en 5 étapes",
    "📊 Infographie: Les chiffres clés du maintien à domicile"
  ];
  const items = [];
  for (let i = 0; i < ideas.length; i++) {
    items.push(
      <div key={i} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900/70 transition-colors cursor-pointer">
        <span className="text-sm">{ideas[i]}</span>
      </div>
    );
  }
  return items;
}

function KPICard({ icon: Icon, label, value, change, color, invertChange = false }) {
  const isPositive = invertChange ? change < 0 : change > 0;
  const colorClasses = {
    pink: 'from-pink-500/20 to-pink-600/10 border-pink-500/30',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
  };
  const iconColors = { pink: 'text-pink-400', green: 'text-green-400', blue: 'text-blue-400', purple: 'text-purple-400' };
  
  return (
    <div className={"bg-gradient-to-br border rounded-xl p-5 " + colorClasses[color]}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={"w-5 h-5 " + iconColors[color]} />
        {change !== undefined && (
          <span className={isPositive ? "flex items-center gap-1 text-xs text-green-400" : "flex items-center gap-1 text-xs text-red-400"}>
            {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(change)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
}

function SocialCard({ platform, icon: Icon, followers, engagement, posts, colorClass }) {
  return (
    <div className={"bg-gradient-to-br border rounded-xl p-5 " + colorClass}>
      <div className="flex items-center gap-3 mb-4">
        <Icon className="w-6 h-6" />
        <span className="font-semibold">{platform}</span>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-400">Abonnés</span>
          <span className="font-medium">{followers.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Engagement</span>
          <span className="font-medium">{engagement}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Posts/mois</span>
          <span className="font-medium">{posts}</span>
        </div>
      </div>
    </div>
  );
}

export default function MarketingDashboard({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sourceStats, setSourceStats] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [hasNewReport, setHasNewReport] = useState(false);

  useEffect(() => { loadData(); checkNewReport(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const filtersResp = await authFetch(API + "/search/filters");
      if (filtersResp.ok) {
        const data = await safeJson(filtersResp);
        const sources = data.source?.values || [];
        const newSourceStats = [];
        for (let i = 0; i < sources.length; i++) {
          newSourceStats.push({ name: sources[i], count: Math.floor(Math.random() * 50) + 10 });
        }
        setSourceStats(newSourceStats);
      }

      setStats({
        total_leads: 847,
        leads_this_month: 124,
        leads_growth: 12.5,
        conversion_rate: 15.2,
        avg_cost_per_lead: 22.50,
        social_followers: 2450,
        email_subscribers: 1890,
        website_visits: 15420
      });

      setCampaigns([
        { id: 1, name: "Campagne Hiver 2025", status: "active", leads: 45, budget: 2000, spent: 1250, roi: 180 },
        { id: 2, name: "Google Ads MaPrimeAdapt", status: "active", leads: 89, budget: 5000, spent: 3200, roi: 220 },
        { id: 3, name: "Facebook Seniors", status: "paused", leads: 23, budget: 1500, spent: 800, roi: 145 },
        { id: 4, name: "Partenariats CCAS", status: "active", leads: 67, budget: 0, spent: 0, roi: 0 },
      ]);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const checkNewReport = async () => {
    try {
      const res = await authFetch(API + "/google-ads/new-report-flag");
      const data = await safeJson(res);
      setHasNewReport(data.has_new_report);
    } catch {}
  };

  const onAdsSettingsClick = () => {
    setActiveTab("ads_settings");
    if (hasNewReport) {
      setHasNewReport(false);
      authFetch(API + "/google-ads/mark-report-read", { method: "POST" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={onBack} data-testid="marketing-back-btn" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">📣 Marketing & Communication</h1>
                  <p className="text-sm text-slate-400">Performance marketing & acquisition leads</p>
                </div>
              </div>
            </div>
            <button onClick={loadData} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">
              <RefreshCw className={loading ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
              Actualiser
            </button>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setActiveTab("overview")} data-testid="tab-overview" className={activeTab === "overview" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-pink-500/20 text-pink-400 border border-pink-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <BarChart2 className="w-4 h-4" />Vue d'ensemble
            </button>
            <button onClick={() => setActiveTab("googleads")} data-testid="tab-googleads" className={activeTab === "googleads" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-blue-500/20 text-blue-400 border border-blue-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <TrendingUp className="w-4 h-4" />Google Ads
            </button>
            <button onClick={() => setActiveTab("sources")} data-testid="tab-sources" className={activeTab === "sources" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-pink-500/20 text-pink-400 border border-pink-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <Target className="w-4 h-4" />Sources de leads
            </button>
            <button onClick={() => setActiveTab("campaigns")} data-testid="tab-campaigns" className={activeTab === "campaigns" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-pink-500/20 text-pink-400 border border-pink-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <Megaphone className="w-4 h-4" />Campagnes
            </button>
            <button onClick={() => setActiveTab("social")} data-testid="tab-social" className={activeTab === "social" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-pink-500/20 text-pink-400 border border-pink-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <Share2 className="w-4 h-4" />Réseaux sociaux
            </button>
            <button onClick={onAdsSettingsClick} data-testid="tab-ads-settings" className={activeTab === "ads_settings" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-amber-500/20 text-amber-400 border border-amber-500/30 relative" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800 relative"}>
              <Settings className="w-4 h-4" />Param. Ads
              {hasNewReport && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" data-testid="new-report-badge" />}
            </button>
            <button onClick={() => setActiveTab("geo_leaks")} data-testid="tab-geo-leaks" className={activeTab === "geo_leaks" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-red-500/20 text-red-400 border border-red-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <MapPin className="w-4 h-4" />Fuites Hors Zone
            </button>
            <button onClick={() => setActiveTab("bid_optimizer")} data-testid="tab-bid-optimizer" className={activeTab === "bid_optimizer" ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-blue-500/20 text-blue-400 border border-blue-500/30" : "flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-slate-800/50 text-slate-400 hover:bg-slate-800"}>
              <Zap className="w-4 h-4" />Optimisation Encheres
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-pink-400 animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === "overview" && stats && (
              <div className="space-y-6">
                {renderKPICards(stats)}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-pink-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.total_leads}</p>
                        <p className="text-sm text-slate-400">Total leads générés</p>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-pink-500 to-rose-500" style={{ width: '75%' }} />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Objectif annuel: 1200 leads</p>
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Share2 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.social_followers.toLocaleString()}</p>
                        <p className="text-sm text-slate-400">Abonnés réseaux sociaux</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex items-center gap-1 text-xs text-slate-400"><Facebook className="w-3 h-3" /> 1.2k</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400"><Linkedin className="w-3 h-3" /> 850</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400"><Instagram className="w-3 h-3" /> 400</span>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.email_subscribers.toLocaleString()}</p>
                        <p className="text-sm text-slate-400">Abonnés newsletter</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">Taux d'ouverture moyen: 24%</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-pink-400" />Évolution des leads (6 derniers mois)
                  </h3>
                  <div className="h-48 flex items-end gap-4">
                    {renderBarChart()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "googleads" && (
              <div className="space-y-8">
                <GoogleAdsSection />
                <div className="border-t border-slate-700/50 pt-8">
                  <GoogleAdsROI />
                </div>
              </div>
            )}

            {activeTab === "sources" && (
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                  <h3 className="font-semibold mb-6 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-400" />Répartition des leads par source
                  </h3>
                  <div className="space-y-4">
                    {renderSourceBars(sourceStats)}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "campaigns" && (
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-700/50">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Megaphone className="w-5 h-5 text-pink-400" />Campagnes marketing actives
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-700/50">
                    {renderCampaigns(campaigns)}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "social" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SocialCard platform="Facebook" icon={Facebook} followers={1200} engagement={4.2} posts={12} colorClass="from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400" />
                  <SocialCard platform="LinkedIn" icon={Linkedin} followers={850} engagement={6.8} posts={8} colorClass="from-sky-500/20 to-sky-600/10 border-sky-500/30 text-sky-400" />
                  <SocialCard platform="Instagram" icon={Instagram} followers={400} engagement={3.1} posts={15} colorClass="from-pink-500/20 to-pink-600/10 border-pink-500/30 text-pink-400" />
                  <SocialCard platform="YouTube" icon={Youtube} followers={125} engagement={8.5} posts={4} colorClass="from-red-500/20 to-red-600/10 border-red-500/30 text-red-400" />
                </div>

                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />Idées de contenu suggérées par SAN-IA
                  </h3>
                  <div className="space-y-3">
                    {renderContentIdeas()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "ads_settings" && <GoogleAdsSettings />}

            {activeTab === "geo_leaks" && <GeoLeaksPanel />}

            {activeTab === "bid_optimizer" && <BidOptimizerPanel />}
          </>
        )}
      </main>

      <SanIAChat poleId="marketing_com" poleName="Marketing & Com" />
    </div>
  );
}
